import t from '@jetshop/intl';
import React, { useState, useEffect, useMemo } from 'react';
import styled, { css, cx } from 'react-emotion';
import storeLocatorQuery from './StoreLocator.gql';
import { useQuery } from 'react-apollo';
import FilterablePage from '../Layout/FilterablePage/FilterablePage';
import { theme } from '../Theme';
import { EztradStyling } from './EztradStyling';
import useShopId from '../../utils/useShopId';
import { Link } from 'react-router-dom';
import { Shortcodes } from '@jetshop/flight-shortcodes';
import Image from '@jetshop/ui/Image';
import AnimatedLink from '../ui/AnimatedLink';

const containerStyling = css`
  .items-container {
    display: flex;
    flex-wrap: wrap;
    margin: 6.625rem -0.78125rem 0;

    .column {
      width: calc(33.3333% - 1.5625rem);
      margin: 0 0.78125rem 6.5rem;
      .heading-container {
        min-height: 58px;
        margin-bottom: 1rem;
        h2 {
          font-weight: 700;
          font-size: 1.5rem;
          letter-spacing: 0.01em;
          color: ${theme.colors.green};
          margin-bottom: 4px;
        }
        .subtitle {
          font-weight: 500;
          letter-spacing: 0.01em;
          color: ${theme.colors.green};
          font-size: 1rem;
        }
      }
      .info-item {
        font-size: 0.875rem;
        line-height: 180%;
        color: ${theme.colors.almostBlack};
        margin-bottom: 2rem;
        :last-of-type {
          margin-bottom: 1.375rem;
        }
        h3 {
          font-weight: 500;
          font-size: 0.9375rem;
          letter-spacing: 0.05em;
          font-variant: small-caps;
          text-transform: lowercase;
        }
      }
      .store-link {
        text-decoration: none;
        border: 1px solid ${theme.colors.darkBeige};
        padding: 0.5rem 1rem;
        font-weight: 500;
        font-size: 0.9375rem;
        letter-spacing: 0.05em;
        font-variant: small-caps;
        text-transform: lowercase;
        color: ${theme.colors.almostBlack};
      }
    }
  }

  ${theme.below.md} {
    .items-container {
      margin-top: 3.25rem;
      .column {
        width: calc(50% - 1.5625rem);
        .heading-container {
          min-height: 36px;
        }
        .info-item {
          margin-bottom: 1.5625rem;
        }
      }
    }
  }

  ${theme.below.sm} {
    .items-container {
      margin-left: 0;
      margin-right: 0;

      .column {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
`;

const StyledImage = styled(Image)`
  margin-bottom: 1rem;
`;

export function storeIdListStringToObject(storeIds) {
  const storeIdsSplitted = storeIds.split(',');
  const storeIdList = [];
  for (let x = 0; x < storeIdsSplitted.length; x++) {
    const id = storeIdsSplitted[x].split(':');
    storeIdList.push({
      storeId: parseInt(id[0].trim()),
      categoryId: parseInt(id[1].trim())
    });
  }

  return storeIdList;
}

const StoreComponent = ({
  address,
  openHours,
  name,
  city,
  link,
  images,
  isEztrad
}) => (
  <div className="column">
    {images && (
      <StyledImage
        src={images}
        aspect={isEztrad ? '4:3' : '367:160'}
        sizes={[1, 1, 1 / 2, 1 / 3, '500px']}
        cover
      />
    )}
    <div className="heading-container">
      <h2>{city}</h2>
      {name && <span className="subtitle">{name}</span>}
    </div>
    {openHours && (
      <div className="info-item">
        <h3>{t('Opening hours')}</h3>
        <div dangerouslySetInnerHTML={{ __html: openHours }} />
      </div>
    )}
    {address && (
      <div className="info-item">
        <h3>{t('Address')}</h3>
        <div dangerouslySetInnerHTML={{ __html: address }} />
      </div>
    )}
    {link &&
      (isEztrad ? (
        <AnimatedLink className="store-link" to={link}>
          {t('Read more')}
        </AnimatedLink>
      ) : (
        <Link className="store-link" to={link}>
          {t('Read more')}
        </Link>
      ))}
  </div>
);

const StoreLocator = ({ category, storeIds, description }) => {
  const [cacheHit, setCacheHit] = useState(false);
  const isEztrad = useShopId() === 'eztrad';

  const { data, loading } = useQuery(storeLocatorQuery);
  const stores = data?.stores || [];

  useEffect(() => {
    if (!loading) setCacheHit(true);
  }, [loading, setCacheHit]);

  const storeIdList = useMemo(() => {
    return storeIdListStringToObject(storeIds);
  }, [storeIds]);

  const { storeList, buttonList } = useMemo(() => {
    const storeList = [];
    const buttonList = [{ key: '', buttonText: t('See all') }];

    stores.forEach(({ name, city, address1, address2, openHours, id }) => {
      if (!buttonList.find(({ key }) => key === city))
        buttonList.push({ key: city });

      const categoryId = storeIdList.find(store => store.storeId === id)
        ?.categoryId;
      const subcategory = category.subcategories.find(
        category => category.id === categoryId
      );
      const path = subcategory?.primaryRoute?.path;
      const images = subcategory?.images;

      storeList.push({
        key: city,
        Item: () => (
          <StoreComponent
            address={`${address1 ? address1 : ''}${address2 ? address2 : ''}`}
            city={city}
            name={name}
            openHours={openHours}
            link={path}
            images={images}
            isEztrad={isEztrad}
          />
        )
      });
    });
    return { storeList, buttonList };
  }, [stores, storeIdList, category]);

  return (
    <FilterablePage
      pageHeaderProps={{
        title: category.name,
        content: description
      }}
      itemList={storeList}
      buttonList={buttonList}
      cacheHit={cacheHit}
      defaultMobileButtonText={t('Filter by city')}
      isMobileDropdown
      className={cx(containerStyling, isEztrad && EztradStyling)}
    />
  );
};

const StoreLocatorPropsWrapper = props => {
  const { category } = props;

  const components = useMemo(() => {
    return {
      stores: props => <StoreLocator category={category} {...props} />
    };
  }, [category]);

  return <Shortcodes content={category.content} components={components} />;
};

export default StoreLocatorPropsWrapper;
